import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaArrowUp,
  FaHeart,
  FaShieldAlt,
  FaHeadset,
  FaRocket,
  FaStar,
  FaCog,
  FaChartLine,
  FaLifeRing,
  FaCrown,
} from "react-icons/fa";
import { motion } from "framer-motion";
import { useAuth } from "../hooks/use-auth";
import BookDemoPopup from "./BookDemoPopup";

const Footer: React.FC = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { isAuthenticated } = useAuth();

  // Check if user has active subscription
  const hasActiveSubscription =
    localStorage.getItem("plan_status") === "active";
  const businessName = localStorage.getItem("business_name");

  const openPopup = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Render different CTA sections based on user status
  const renderCTASection = () => {
    if (isAuthenticated && hasActiveSubscription) {
      // For authenticated users with active subscription
      return (
        <div className="relative py-12 px-6">
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-slate-800/50 border border-slate-700 p-8 md:p-12 text-center backdrop-blur-sm">
              <div className="relative z-10">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  className="flex items-center justify-center space-x-3 mb-6"
                >
                  <div className="p-3 bg-blue-500/20 rounded-full">
                    <FaCrown className="text-blue-400 text-2xl" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white">
                    Welcome back{businessName ? `, ${businessName}` : ""}!
                  </h3>
                </motion.div>
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-slate-400 mb-8 text-lg max-w-2xl mx-auto"
                >
                  Your restaurant cockpit is ready. Check your latest insights and orders.
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                >
                  <Link
                    to="/main"
                    className="flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-8 py-4 rounded-xl font-bold hover:shadow-[0_0_20px_rgba(79,70,229,0.4)] transition-all duration-300 transform hover:-translate-y-1"
                  >
                    <FaChartLine />
                    <span>Go to Dashboard</span>
                  </Link>
                  <button
                    onClick={openPopup}
                    className="flex items-center space-x-2 px-8 py-4 rounded-xl font-bold text-white border border-slate-600 hover:bg-slate-800 hover:border-slate-500 transition-all duration-300"
                  >
                    <FaLifeRing />
                    <span>Get Support</span>
                  </button>
                </motion.div>
              </div>
              {/* Decorative elements */}
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl -mr-32 -mt-32" />
              <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl -ml-32 -mb-32" />
            </div>
          </div>
        </div>
      );
    } else {
      // For non-authenticated users or users without subscription
      return (
        <div className="relative py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700/50 p-8 md:p-16 text-center shadow-2xl">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(249,115,22,0.1),transparent_50%)]" />
              
              <div className="relative z-10 max-w-3xl mx-auto space-y-8">
                <motion.h3 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  className="text-3xl md:text-5xl font-bold text-white bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400"
                >
                  Ready to Transform Your Restaurant?
                </motion.h3>
                
                <motion.p 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-slate-400 text-lg md:text-xl leading-relaxed"
                >
                  Join thousands of forward-thinking restaurants already using Willovate Resto to streamline operations and boost revenue.
                </motion.p>
                
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4"
                >
                  <button
                    onClick={openPopup}
                    className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-red-600 text-white px-8 py-4 rounded-xl font-bold hover:shadow-[0_0_20px_rgba(249,115,22,0.4)] transition-all duration-300 transform hover:-translate-y-1"
                  >
                    Start Free Trial
                  </button>
                  <Link
                    to="/demo"
                    className="w-full sm:w-auto px-8 py-4 rounded-xl font-bold text-white border border-slate-600 hover:bg-slate-800 hover:border-slate-500 transition-all duration-300 flex items-center justify-center space-x-2"
                  >
                    <FaYoutube className="text-red-500 text-xl" />
                    <span>Watch Demo</span>
                  </Link>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  // Render different quick links based on user status
  const renderQuickLinks = () => {
    if (isAuthenticated && hasActiveSubscription) {
      // Links for authenticated users with subscription
      return [
        { name: "Dashboard", path: "/main", icon: <FaChartLine /> },
        { name: "Menu Management", path: "/menu", icon: <FaStar /> },
        { name: "Orders", path: "/order-list", icon: <FaRocket /> },
        { name: "Account Settings", path: "/admin-profile", icon: <FaCog /> },
        { name: "Billing", path: "/payment", icon: <FaCrown /> },
      ];
    } else {
      // Links for non-authenticated users
      return [
        { name: "Home", path: "/", icon: null },
        { name: "About Us", path: "/about", icon: null },
        { name: "How It Works", path: "/how-it-works", icon: null },
        { name: "Pricing Plans", path: "/plan-section", icon: null },
        { name: "FAQ", path: "/faq", icon: null },
      ];
    }
  };

  return (
    <>
      {isAuthenticated && hasActiveSubscription ? (
        // Minimal footer for authenticated users with subscription
        <footer className="bg-slate-900 border-t border-slate-800 mt-auto">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center space-x-3">
                <img
                  src="/Willovate_Resto_Logo.png"
                  alt="Willovate Resto"
                  className="h-8 w-auto object-contain brightness-0 invert"
                />
                <span className="text-slate-500 text-sm border-l border-slate-700 pl-3 ml-3">
                  © {new Date().getFullYear()} Willovate Resto
                </span>
              </div>

              <div className="flex items-center flex-wrap justify-center gap-6 text-sm">
                <button onClick={openPopup} className="text-slate-400 hover:text-blue-400 transition-colors">Support</button>
                <Link to="/admin-profile" className="text-slate-400 hover:text-blue-400 transition-colors">Settings</Link>
                <Link to="/privacy-policy" className="text-slate-400 hover:text-blue-400 transition-colors">Privacy</Link>
                <Link to="/t&c" className="text-slate-400 hover:text-blue-400 transition-colors">Terms</Link>
              </div>
            </div>
          </div>
        </footer>
      ) : (
        // Full marketing footer
        <footer className="relative bg-[#0B0F19] text-slate-300 overflow-hidden font-sans border-t border-slate-800">
          
          {/* Subtle Grid Background */}
          <div className="absolute inset-0 pointer-events-none opacity-20">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/20 to-transparent" />
          </div>

          <div className="relative z-10">
            {renderCTASection()}

            <div className="max-w-7xl mx-auto px-6 py-12 md:py-20">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 lg:gap-8">
                
                {/* Brand Column (Span 4) */}
                <div className="lg:col-span-4 space-y-8">
                  <div className="flex items-center space-x-3">
                    <img
                      src="/Willovate_Resto_Logo.png"
                      alt="Willovate Resto"
                      className="h-10 w-auto object-contain brightness-0 invert" 
                    />
                    <div>
                      <h3 className="text-xl font-bold text-white tracking-tight">Willovate Resto</h3>
                      <p className="text-slate-500 text-sm">Restaurant Operating System</p>
                    </div>
                  </div>

                  <p className="text-slate-400 leading-relaxed max-w-sm">
                    Empowering modern restaurants with an all-in-one platform for ordering, kitchen management, and growth analytics.
                  </p>

                  <div className="flex gap-4">
                    {[
                      { icon: <FaInstagram />, href: "https://www.instagram.com/willovate_/", color: "hover:bg-pink-600" },
                      { icon: <FaLinkedinIn />, href: "https://www.linkedin.com/company/willovate-private-limited", color: "hover:bg-blue-600" },
                      { icon: <FaYoutube />, href: "https://youtube.com/@willovate", color: "hover:bg-red-600" },
                    ].map((social, idx) => (
                      <a
                        key={idx}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 transition-all duration-300 hover:text-white ${social.color} hover:-translate-y-1`}
                      >
                        {social.icon}
                      </a>
                    ))}
                  </div>
                </div>

                {/* Spacer (Span 1) */}
                <div className="hidden lg:block lg:col-span-1" />

                {/* Quick Links (Span 2) */}
                <div className="lg:col-span-2 space-y-6">
                  <h4 className="text-lg font-semibold text-white">Platform</h4>
                  <ul className="space-y-4">
                    {renderQuickLinks().map((link) => (
                      <li key={link.name}>
                        <Link 
                          to={link.path} 
                          className="text-slate-400 hover:text-blue-400 transition-colors flex items-center gap-2 group"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-600 group-hover:bg-blue-400 transition-colors" />
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Features (Span 2) */}
                <div className="lg:col-span-2 space-y-6">
                  <h4 className="text-lg font-semibold text-white">Features</h4>
                  <ul className="space-y-4">
                    {[
                      { name: "QR Ordering", icon: <FaRocket /> },
                      { name: "Kitchen Display", icon: <FaHeadset /> }, // Using Headset as proxy or remove icon logic
                      { name: "Inventory", icon: <FaStar /> },
                      { name: "Analytics", icon: <FaChartLine /> }
                    ].map((item) => (
                      <li key={item.name}>
                        <Link to="/features" className="text-slate-400 hover:text-blue-400 transition-colors flex items-center gap-2 group">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-600 group-hover:bg-blue-400 transition-colors" />
                          {item.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Contact (Span 3) */}
                <div className="lg:col-span-3 space-y-6">
                  <h4 className="text-lg font-semibold text-white">Contact Us</h4>
                  <ul className="space-y-4">
                    <li className="flex items-start gap-4 text-slate-400">
                      <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center shrink-0 text-blue-500">
                        <FaMapMarkerAlt size={14} />
                      </div>
                      <span className="text-sm">Nagpur, Maharashtra,<br />India</span>
                    </li>
                    <li className="flex items-center gap-4 text-slate-400">
                      <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center shrink-0 text-blue-500">
                        <FaPhone size={14} />
                      </div>
                      <span className="text-sm">+91 93223 46482</span>
                    </li>
                    <li className="flex items-center gap-4 text-slate-400">
                      <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center shrink-0 text-blue-500">
                        <FaEnvelope size={14} />
                      </div>
                      <span className="text-sm">admin@willovate.in</span>
                    </li>
                    <li className="flex items-center gap-4 text-slate-400 pt-2">
                       <span className="flex items-center gap-2 text-xs bg-slate-800/50 px-3 py-1 rounded-full border border-slate-700">
                         <FaShieldAlt className="text-green-500" size={12} /> SSL Secured
                       </span>
                       <span className="flex items-center gap-2 text-xs bg-slate-800/50 px-3 py-1 rounded-full border border-slate-700">
                         <FaHeart className="text-red-500" size={12} /> Made in India
                       </span>
                    </li>
                  </ul>
                </div>

              </div>
            </div>

            {/* Footer Bottom */}
            <div className="border-t border-slate-800 bg-slate-900/50">
              <div className="max-w-7xl mx-auto px-6 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  
                  {/* Copyright */}
                  <div className="text-slate-500 text-sm text-center md:text-left">
                    <p>© {new Date().getFullYear()} Willovate Resto. All rights reserved.</p>
                  </div>

                  {/* Legal Links */}
                  <div className="flex items-center gap-8 text-sm text-slate-500">
                    <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
                    <Link to="/t&c" className="hover:text-white transition-colors">Terms of Service</Link>
                    <Link to="/sitemap" className="hover:text-white transition-colors">Sitemap</Link>
                  </div>

                  {/* Scroll Top */}
                  <button
                    onClick={scrollToTop}
                    className="w-10 h-10 rounded-xl bg-slate-800 hover:bg-blue-600 text-slate-400 hover:text-white flex items-center justify-center transition-all duration-300 hover:-translate-y-1"
                  >
                    <FaArrowUp size={14} />
                  </button>

                </div>
              </div>
            </div>

          </div>
        </footer>
      )}

      {/* Book Demo Popup */}
      <BookDemoPopup isOpen={isPopupOpen} onClose={closePopup} />
    </>
  );
};

export default Footer;
